import React, { useState } from 'react';

interface CustomServerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAddServer: (url: string) => Promise<{ success: boolean; message: string }>;
  onImportServers: (url: string) => Promise<{ success: boolean; message: string }>;
}

type ModalTab = 'manual' | 'import';

const CustomServerModal: React.FC<CustomServerModalProps> = ({ isOpen, onClose, onAddServer, onImportServers }) => {
  const [activeTab, setActiveTab] = useState<ModalTab>('manual');
  
  const [manualUrl, setManualUrl] = useState('');
  const [importUrl, setImportUrl] = useState('');

  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState<{ type: 'error' | 'success'; message: string } | null>(null);

  if (!isOpen) return null;
  
  const resetState = () => {
    setManualUrl('');
    setImportUrl('');
    setIsLoading(false);
    setStatus(null);
  };
  
  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleManualSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
    if (!manualUrl) {
      setStatus({ type: 'error', message: 'Please enter a URL.' });
      return;
    }
    try {
      const parsedUrl = new URL(manualUrl);
       if (!parsedUrl.protocol.startsWith('http')) {
        throw new Error('Protocol must be http or https.');
    }
    } catch (_) {
      setStatus({ type: 'error', message: 'Invalid URL format. Please include http:// or https:// and a path to a file.' });
      return;
    }

    setIsLoading(true);
    const result = await onAddServer(manualUrl);
    setIsLoading(false);

    if (result.success) {
      handleClose();
    } else {
      setStatus({ type: 'error', message: result.message });
    }
  };
  
  const handleImportSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus(null);
     if (!importUrl) {
      setStatus({ type: 'error', message: 'Please enter a URL for the server list.' });
      return;
    }
    setIsLoading(true);
    const result = await onImportServers(importUrl);
    setIsLoading(false);

    if (result.success) {
        setStatus({ type: 'success', message: result.message });
        // Don't close automatically, so user can see success message
    } else {
        setStatus({ type: 'error', message: result.message });
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={handleClose} role="dialog" aria-modal="true" aria-labelledby="custom-server-title">
      <div className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg shadow-xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 id="custom-server-title" className="text-xl font-bold text-slate-900 dark:text-white">Manage Custom Servers</h2>
          <button onClick={handleClose} className="text-gray-400 dark:text-slate-400 hover:text-gray-800 dark:hover:text-white text-2xl leading-none" aria-label="Close modal">&times;</button>
        </div>
        
        <div className="border-b border-gray-200 dark:border-slate-700 mb-4">
            <nav className="-mb-px flex space-x-4" aria-label="Tabs">
                <button onClick={() => { setActiveTab('manual'); setStatus(null); }} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'manual' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-slate-300'}`}>
                    Add Manually
                </button>
                <button onClick={() => { setActiveTab('import'); setStatus(null); }} className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'import' ? 'border-cyan-500 text-cyan-600 dark:text-cyan-400' : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-slate-300'}`}>
                    Import from URL
                </button>
            </nav>
        </div>

        {activeTab === 'manual' && (
          <form onSubmit={handleManualSubmit}>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
              Enter the full URL to a speed test file. The server must have a permissive CORS policy (Access-Control-Allow-Origin: *) to work correctly.
            </p>
            <label htmlFor="server-url" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Server URL</label>
            <input
              id="server-url"
              type="url"
              value={manualUrl}
              onChange={(e) => setManualUrl(e.target.value)}
              placeholder="https://speed.example.com/100MB.bin"
              className="w-full bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md p-2 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
              required
            />
            <div className="mt-6 flex justify-end gap-4">
              <button type="submit" disabled={isLoading} className="px-4 py-2 bg-cyan-600 text-white font-semibold rounded-full hover:bg-cyan-500 transition disabled:opacity-50 disabled:cursor-wait">
                {isLoading ? 'Testing...' : 'Test & Add Server'}
              </button>
            </div>
          </form>
        )}
        
        {activeTab === 'import' && (
            <form onSubmit={handleImportSubmit}>
              <p className="text-sm text-gray-500 dark:text-slate-400 mb-4">
                Enter a URL to a JSON file containing a list of servers, like the API endpoint from a Speedtest Tracker instance.
              </p>
              <label htmlFor="import-url" className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">Import URL</label>
              <input
                id="import-url"
                type="url"
                value={importUrl}
                onChange={(e) => setImportUrl(e.target.value)}
                placeholder="https://my-tracker.com/api/v1/ookla/list-servers"
                className="w-full bg-gray-100 dark:bg-slate-700 border border-gray-300 dark:border-slate-600 rounded-md p-2 text-slate-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:ring-2 focus:ring-cyan-500 focus:outline-none"
                required
              />
               <div className="mt-6 flex justify-end gap-4">
                <button type="submit" disabled={isLoading} className="px-4 py-2 bg-cyan-600 text-white font-semibold rounded-full hover:bg-cyan-500 transition disabled:opacity-50 disabled:cursor-wait">
                  {isLoading ? 'Importing...' : 'Import Servers'}
                </button>
              </div>
            </form>
        )}

        {status && (
            <div className={`text-sm mt-4 p-3 rounded-md ${status.type === 'error' ? 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300'}`}>
                {status.message}
            </div>
        )}

      </div>
    </div>
  );
};

export default CustomServerModal;